import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { CustomInput, CustomTextarea, CustomToggle } from '../Components/CustomFormElements';
import PageLoader from '../Components/PageLoader';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { MarkerApiProvider, useMarkerApi } from '../Services/MarkerApiProvider';

import { isEmpty } from '../utils/validationUtil';

function MarkerContent({ canEdit }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId, markerId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { getMarkerById, createMarker, updateMarker } = useMarkerApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isNew, setIsNew] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);

  const [name, setName] = useState('');
  const [objectAttributes, setObjectAttributes] = useState([]);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const f = async () => {
      if (isNew === false) {
        setLoading(true);
        try {
          const response = await getMarkerById(markerId);
          setName(response.name);
          setObjectAttributes(response.object_attributes || []);
          setIsActive(response.is_active === true ? true : false);
        } catch (error) {
          showAlert('fail', 'Failed to fetch marker');
        } finally {
          setLoading(false);
        }
      }
    };
    f();
  }, [isNew]);

  useEffect(() => {
    setIsNew(markerId === 'new-marker');
    setIsDisabled(!canEdit);
  }, [markerId]);

  const onSave = async () => {
    if (isEmpty(name)) {
      showAlert('fail', 'Please enter marker name', 'error');
      return;
    }

    const data = {
      name: name,
      object_attributes: objectAttributes,
      is_active: isActive,
    };

    setLoading(true);

    try {
      if (isNew) {
        await createMarker(data);
        showAlert('success', 'Marker was created successfully');
      } else {
        await updateMarker(markerId, data);
        showAlert('success', 'Marker was updated successfully');
      }
      navigate(`/organizations/${organizationId}/markers`, { state: { organizationId } });
    } catch (error) {
      showAlert('fail', 'Failed to save marker');
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/markers`, { state: { organizationId } });
  };

  const addAttribute = () => {
    setObjectAttributes([...objectAttributes, { attribute_name: '', description: '' }]);
  };

  const removeAttribute = (index) => {
    const newAttributes = objectAttributes.filter((_, i) => i !== index);
    setObjectAttributes(newAttributes);
  };

  const updateAttribute = (index, field, value) => {
    const newAttributes = [...objectAttributes];
    newAttributes[index] = { ...newAttributes[index], [field]: value };
    setObjectAttributes(newAttributes);
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <h2 className="h2-title">{name || 'New Marker'}</h2>
          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Main Settings</h4>

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Marker Name</div>
                  <div className="small-font">Enter marker name</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="marker-name"
                    label="Marker Name"
                    placeholder="Enter marker name"
                    type="text"
                    icon={<i className="fa-solid fa-tag"></i>}
                    value={name}
                    onChange={(id, value) => setName(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Activate Marker</div>
                  <div className="small-font">
                    Set marker to be active or inactive to enable or disable it
                  </div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomToggle
                    id="toggle-status"
                    label={isActive ? 'Active' : 'Inactive'}
                    checked={isActive}
                    onChange={(id, value) => setIsActive(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>
            </div>

            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Schema Attributes</h4>
              <div className="small-font mb-2">Define the attributes schema for this marker</div>

              {objectAttributes.map((attr, index) => (
                <div key={index} className="attribute-row mb-2 p-2 border-yes radius-yes">
                  <div className="input-block d-flex flex-row justify-between">
                    <div className="input-description">
                      <div className="input-title">Attribute Name</div>
                      <div className="small-font">Enter attribute name</div>
                    </div>
                    <div className="align-wrapper d-flex align-center gap">
                      <CustomInput
                        id={`attr-name-${index}`}
                        label="Attribute Name"
                        placeholder="Enter attribute name"
                        type="text"
                        value={attr.attribute_name}
                        onChange={(id, value) => updateAttribute(index, 'attribute_name', value)}
                        disabled={isDisabled}
                      />
                      {!isDisabled && (
                        <Button
                          variant="danger"
                          size="small"
                          onClick={() => removeAttribute(index)}
                        >
                          <i className="fa-solid fa-trash"></i>
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="input-block d-flex flex-row justify-between">
                    <div className="input-description">
                      <div className="input-title">Description</div>
                      <div className="small-font">Enter description (optional)</div>
                    </div>
                    <div className="align-wrapper d-flex align-center">
                      <CustomTextarea
                        id={`attr-desc-${index}`}
                        label="Description"
                        name={`description-${index}`}
                        type="text"
                        addedText={attr.description}
                        onChange={(value) => updateAttribute(index, 'description', value)}
                        resizable={false}
                        disabled={isDisabled}
                        height="80px"
                      />
                    </div>
                  </div>
                </div>
              ))}

              {!isDisabled && (
                <Button variant="secondary" size="medium" onClick={addAttribute}>
                  <i className="fa-solid fa-plus"></i> Add Attribute
                </Button>
              )}
            </div>

            <div className="buttons-container d-flex justify-end">
              <Button variant="primary" size="medium" disabled={isDisabled} onClick={onSave}>
                Save
              </Button>
              <Button variant="danger" size="medium" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Marker({ canEdit }) {
  return (
    <ConfirmationProvider>
      <MarkerApiProvider>
        <MarkerContent canEdit={canEdit} />
      </MarkerApiProvider>
    </ConfirmationProvider>
  );
}

export default Marker;
